const IMAGE_SELECTOR = '[data-js-native-lazy] img[loading="lazy"]';
const WRAPPER_SELECTOR = '[data-js-native-lazy]';
const REVEAL_SELECTOR = '[data-scroll-reveal]';
const REVEAL_START_EVENT = 'hei:scrollRevealStart';
const REVEAL_COMPLETE_EVENT = 'hei:scrollRevealComplete';
const PENDING_CLASS = 'js-native-lazy--pending';
const LOADED_CLASS = 'js-native-lazy--loaded';
const ERROR_CLASS = 'js-native-lazy--error';
const DEFAULT_REVEAL_START_DELAY = 250;
const LOAD_TEST_DELAY = 0;
const FADE_DURATION = 450;
const FADE_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const ANIMATING_CLASS = 'js-native-lazy--animating';
const PLACEHOLDER_OPACITY_PROPERTY = '--js-native-lazy-placeholder-opacity';

const findElements = (root, selector) => {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    const elements = scope.matches?.(selector) ? [scope] : [];

    return elements.concat(Array.from(scope.querySelectorAll(selector)));
};

const getWrapper = (img) => img.closest(WRAPPER_SELECTOR);

const parseDelay = (value) => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    const delay = Number.parseInt(value, 10);

    return Number.isNaN(delay) ? null : Math.max(0, delay);
};

const parseTimeList = (value) => value
    .split(',')
    .map((time) => time.trim())
    .filter(Boolean)
    .map((time) => {
        const duration = Number.parseFloat(time);

        if (Number.isNaN(duration)) {
            return 0;
        }

        return time.endsWith('ms') ? duration : duration * 1000;
    });

const getRevealTransitionDelay = (reveal) => {
    if (!reveal) {
        return 0;
    }

    const delays = parseTimeList(window.getComputedStyle(reveal).transitionDelay);

    return delays.length ? Math.max(...delays) : 0;
};

const getRevealStartDelay = (img) => {
    const wrapper = getWrapper(img);
    const reveal = wrapper?.closest(REVEAL_SELECTOR);
    const delay = parseDelay(wrapper?.dataset.jsNativeLazyRevealDelay)
        ?? parseDelay(reveal?.dataset.jsNativeLazyRevealDelay)
        ?? parseDelay(document.documentElement.dataset.jsNativeLazyRevealDelay);

    return getRevealTransitionDelay(reveal) + (delay ?? DEFAULT_REVEAL_START_DELAY);
};

const getPendingReveal = (img) => {
    if (
        !document.documentElement.classList.contains('scroll-reveal-enabled')
        || document.documentElement.classList.contains('scroll-reveal-fallback')
        || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
        return null;
    }

    const reveal = getWrapper(img)?.closest(REVEAL_SELECTOR);

    if (!reveal || reveal.dataset.scrollRevealComplete === 'true') {
        return null;
    }

    if (reveal.dataset.scrollRevealInit !== 'true') {
        return null;
    }

    return reveal;
};

const setReady = (img, ready = true) => {
    img.dataset.jsNativeLazyReady = ready ? 'true' : 'false';
};

const resetDecode = (img) => {
    img.dataset.jsNativeLazyDecoded = 'false';
    img.dataset.jsNativeLazyDecoding = 'false';
    img.jsNativeLazyDecodePromise = null;
};

const decodeImage = (img) => {
    if (img.dataset.jsNativeLazyDecoded === 'true' || typeof img.decode !== 'function') {
        return Promise.resolve();
    }

    if (img.jsNativeLazyDecodePromise) {
        return img.jsNativeLazyDecodePromise;
    }

    img.dataset.jsNativeLazyDecoding = 'true';
    img.jsNativeLazyDecodePromise = img.decode()
        .catch(() => {})
        .then(() => {
            img.dataset.jsNativeLazyDecoded = 'true';
            img.dataset.jsNativeLazyDecoding = 'false';
            img.jsNativeLazyDecodePromise = null;
        });

    return img.jsNativeLazyDecodePromise;
};

const markPending = (img) => {
    const wrapper = getWrapper(img);

    if (!wrapper) {
        return;
    }

    wrapper.classList.add(PENDING_CLASS);
    wrapper.classList.remove(LOADED_CLASS, ERROR_CLASS);
};

const applyLoaded = (img) => {
    const wrapper = getWrapper(img);

    if (!wrapper) {
        return;
    }

    wrapper.classList.remove(PENDING_CLASS, ERROR_CLASS, ANIMATING_CLASS);
    wrapper.classList.add(LOADED_CLASS);
    img.classList.add('lazy-loaded');
    img.style.opacity = '';
    wrapper.style.removeProperty(PLACEHOLDER_OPACITY_PROPERTY);
};

const cancelAnimations = (img) => {
    const wrapper = getWrapper(img);

    img.dataset.jsNativeLazyAnimationId = '';
    img.jsNativeLazyAnimations?.forEach((animation) => animation.cancel());
    wrapper?.jsNativeLazyAnimations?.forEach((animation) => animation.cancel());
    img.jsNativeLazyAnimations = [];

    if (wrapper) {
        wrapper.jsNativeLazyAnimations = [];
    }
};

const animateLoaded = (img) => {
    const wrapper = getWrapper(img);

    if (!wrapper || typeof img.animate !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        applyLoaded(img);
        return;
    }

    cancelAnimations(img);

    const animationId = `${Date.now()}-${Math.random()}`;
    img.dataset.jsNativeLazyAnimationId = animationId;
    wrapper.classList.add(ANIMATING_CLASS);
    wrapper.style.setProperty(PLACEHOLDER_OPACITY_PROPERTY, '1');
    img.style.opacity = '0';

    const options = {
        duration: FADE_DURATION,
        easing: FADE_EASING,
        fill: 'forwards',
    };
    const imageAnimation = img.animate([
        { opacity: 0 },
        { opacity: 1 },
    ], options);
    const placeholderAnimation = wrapper.animate([
        { [PLACEHOLDER_OPACITY_PROPERTY]: '1' },
        { [PLACEHOLDER_OPACITY_PROPERTY]: '0' },
    ], options);

    img.jsNativeLazyAnimations = [imageAnimation];
    wrapper.jsNativeLazyAnimations = [placeholderAnimation];

    Promise.allSettled([
        imageAnimation.finished,
        placeholderAnimation.finished,
    ]).then(() => {
        if (img.dataset.jsNativeLazyAnimationId !== animationId) {
            return;
        }

        applyLoaded(img);
        cancelAnimations(img);
    });
};

const decodeAndAnimate = (img, delay = 0) => {
    window.setTimeout(() => {
        decodeImage(img).then(() => {
            animateLoaded(img);
        });
    }, delay + LOAD_TEST_DELAY);
};

const releaseReadyImage = (img, delay = 0) => {
    setReady(img, false);
    decodeAndAnimate(img, delay);
};

const scheduleRevealStartRelease = (img) => {
    if (img.dataset.jsNativeLazyRevealStartQueued === 'true') {
        return;
    }

    img.dataset.jsNativeLazyRevealStartQueued = 'true';

    window.setTimeout(() => {
        img.dataset.jsNativeLazyRevealStartQueued = 'false';

        if (img.dataset.jsNativeLazyReady !== 'true' || !img.complete || img.naturalWidth <= 0) {
            return;
        }

        const reveal = getPendingReveal(img);

        if (reveal && !reveal.classList.contains('is-visible')) {
            return;
        }

        releaseReadyImage(img);
    }, getRevealStartDelay(img));
};

const markLoaded = (img) => {
    const pendingReveal = getPendingReveal(img);

    if (pendingReveal) {
        setReady(img);
        resetDecode(img);

        if (pendingReveal.classList.contains('is-visible')) {
            scheduleRevealStartRelease(img);
        }

        return;
    }

    releaseReadyImage(img);
};

const markError = (img) => {
    const wrapper = getWrapper(img);

    if (!wrapper) {
        return;
    }

    wrapper.classList.remove(PENDING_CLASS, LOADED_CLASS);
    wrapper.classList.remove(ANIMATING_CLASS);
    wrapper.classList.add(ERROR_CLASS);
    setReady(img, false);
    resetDecode(img);
    cancelAnimations(img);
};

const lazy = {
    mutationObserver: null,
    revealCompletionObserver: false,

    init(root = document) {
        window.clearTimeout(window.__heiJsNativeLazyFallback);
        document.documentElement.classList.add('js-native-lazy-enabled');
        document.documentElement.classList.remove('js-native-lazy-fallback');

        this.refresh(root);
        this.observeDom();
        this.observeRevealCompletions();
    },

    refresh(root = document) {
        findElements(root, IMAGE_SELECTOR).forEach((img) => {
            this.prepareImage(img);
        });
    },

    releaseDeferredImages(root) {
        findElements(root, IMAGE_SELECTOR).forEach((img) => {
            if (img.dataset.jsNativeLazyReady !== 'true') {
                return;
            }

            setReady(img, false);

            if (img.complete) {
                if (img.naturalWidth > 0) {
                    markLoaded(img, true);
                } else {
                    markError(img);
                }
            }
        });
    },

    releaseRevealStartedImages(root) {
        findElements(root, IMAGE_SELECTOR).forEach((img) => {
            if (img.dataset.jsNativeLazyReady !== 'true') {
                return;
            }

            scheduleRevealStartRelease(img);
        });
    },

    prepareImage(img, forcePending = false) {
        if (!(img instanceof HTMLImageElement) || !img.matches(IMAGE_SELECTOR)) {
            return;
        }

        const wrapper = getWrapper(img);

        if (!wrapper) {
            return;
        }

        if (!forcePending && (wrapper.classList.contains(LOADED_CLASS) || wrapper.classList.contains(ERROR_CLASS))) {
            return;
        }

        if (forcePending) {
            setReady(img, false);
            resetDecode(img);
        }

        markPending(img);

        const markComplete = () => {
            if (!img.complete) {
                return;
            }

            if (img.naturalWidth > 0) {
                markLoaded(img);
            } else {
                markError(img);
            }
        };

        if (img.dataset.jsNativeLazyObserved === 'true') {
            markComplete();
            return;
        }

        img.addEventListener('load', () => markLoaded(img));
        img.addEventListener('error', () => markError(img));
        img.dataset.jsNativeLazyObserved = 'true';
        markComplete();
    },

    observeDom() {
        if (this.mutationObserver || !document.body) {
            return;
        }

        this.mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (!(node instanceof Element)) {
                            return;
                        }

                        if (node.matches(IMAGE_SELECTOR)) {
                            this.prepareImage(node);
                        }

                        findElements(node, IMAGE_SELECTOR).forEach((img) => {
                            this.prepareImage(img);
                        });
                    });
                }

                if (mutation.type === 'attributes' && mutation.target.matches(IMAGE_SELECTOR)) {
                    this.prepareImage(mutation.target, true);
                }
            });
        });

        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['src', 'srcset'],
        });
    },

    observeRevealCompletions() {
        if (this.revealCompletionObserver) {
            return;
        }

        document.addEventListener(REVEAL_START_EVENT, (event) => {
            this.releaseRevealStartedImages(event.target);
        });

        document.addEventListener(REVEAL_COMPLETE_EVENT, (event) => {
            this.releaseDeferredImages(event.target);
        });

        this.revealCompletionObserver = true;
    },
};

export default lazy;
